import { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { useAddAlert } from '@/hooks/useAddAlert'
import Preloader from '@UI/Preloader'
import Button from '@UI/Button'
import Modal, { ModalContainer } from '@common/Modal'
import { chatApi } from '@/api'
import classes from './../../index.module.css'
import styles from './StudentChatsTab.module.css';
import ActionModal from '@pages/Admin/AllUsers/modals/ActionModal'
import { CloseOutlined, EditOutlined } from '@ant-design/icons'
import {
    AddChatModal,
    EditRoleModal
} from "@pages/Admin/AllUsers/modals/EditStudentModal/components/StudentChatsTab/modals/Modals";

const ROLE_MAP = {
    1: 'member',
    2: 'admin',
    3: 'reader',
}

const ROLE_LABELS = {
    admin:    'администратор',
    member:   'участник',
    reader: 'читатель',
}


const ChatsHeaderRow = () => (
    <div className={classNames(classes.learningDynamicsRowContainer, styles.columns)}>
        <div className={classes.learningDynamicsCell}>Название чата</div>
        <div className={classNames(classes.learningDynamicsCell, classes.justifyCenter)}>
            Роль в чате
        </div>
        <div className={classNames(classes.iconButtonCell, classes.justifyCenter)} />
    </div>
)

const ChatRow = ({ chat, studentId, onEdit, onRemove }) => {
    const member = chat.members?.find(m => m.user_id === studentId || m.id === studentId)
    const role = ROLE_MAP[member?.role] ?? 'member'

    return (
        <div className={classNames(classes.learningDynamicsExpandedRowContainer, styles.columns)}>
            <div className={classes.learningDynamicsCell}>
                {chat.title ?? chat.name}
            </div>
            <div className={classNames(
                classes.learningDynamicsCell,
                classes.justifyCenter,
            )}>
                {ROLE_LABELS[role] ?? 'участник'}
                <div
                    className={classNames(classes.iconButtonCell, classes.iconEditButtonCell)}
                    style={{ cursor: 'pointer' }}
                    onClick={onEdit}
                >
                    <EditOutlined />
                </div>
            </div>
            <div
                className={classNames(classes.iconButtonCell, classes.justifyCenter)}
                style={{ cursor: 'pointer' }}
                onClick={onRemove}
            >
                <CloseOutlined />
            </div>
        </div>
    )
}

const StudentChatsTab = ({ studentId }) => {
    const addAlert = useAddAlert()

    const [chats, setChats]             = useState([])
    const [isLoading, setIsLoading]     = useState(false)
    const [addModal, setAddModal]       = useState(false)
    const [editModal, setEditModal]     = useState(null)
    const [removeModal, setRemoveModal] = useState(null)

    const fetchChats = async () => {
        setIsLoading(true)
        try {
            const res = await chatApi.getChatListV2({
                user_id: studentId,
                type: 'group',
                'per-page': 100,
            })
            const data = res.data?.data ?? res.data ?? []
            setChats(data)
        } catch {
            addAlert({ title: 'Ошибка загрузки чатов', type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => { if (studentId) fetchChats() }, [studentId])

    const getMemberId = async (chatId) => {
        const res = await chatApi.getChat(chatId)
        const members = res.data?.members ?? res.data?.data?.members ?? []
        const member = members.find(m => m.user_id === studentId || m.id === studentId)
        return member?.member_id ?? member?.pivot?.id ?? null
    }

    const handleAddSave = async ({ chat, role }) => {
        try {
            const res = await chatApi.addMember({
                chatId: Number(chat.id),
                users: [{ id: studentId, role }],
            })
            if (res.data?.success || res.data?.status === 'success') {
                addAlert({ title: `Пользователь добавлен в чат «${chat.title ?? chat.name}»` })
                fetchChats()
            } else throw new Error()
        } catch {
            addAlert({ title: 'Не удалось добавить в чат', type: 'error' })
        }
        setAddModal(false)
    }

    const handleEditOpen = async (chat) => {
        try {
            const memberId = await getMemberId(chat.id)
            if (!memberId) throw new Error('member_id не найден')
            setEditModal({ chatId: chat.id, memberId, name: chat.title ?? chat.name, role: chat.member?.role ?? 'member' })
        } catch {
            addAlert({ title: 'Не удалось получить данные участника', type: 'error' })
        }
    }

    const handleEditSave = async (newRole) => {
        try {
            const res = await chatApi.updateRoleMember(editModal.memberId, { role: newRole })
            if (res.data?.success || res.data?.status === 'success') {
                addAlert({ title: 'Роль успешно изменена' })
                fetchChats()
            } else throw new Error()
        } catch {
            addAlert({ title: 'Не удалось изменить роль', type: 'error' })
        }
        setEditModal(null)
    }

    const handleRemoveOpen = async (chat) => {
        try {
            const memberId = await getMemberId(chat.id)
            if (!memberId) throw new Error('member_id не найден')
            setRemoveModal({ memberId, name: chat.title ?? chat.name })
        } catch {
            addAlert({ title: 'Не удалось получить данные участника', type: 'error' })
        }
    }

    const handleRemoveConfirm = async () => {
        try {
            await chatApi.deleteMember(removeModal.memberId)
            addAlert({ title: `Пользователь удалён из чата «${removeModal.name}»` })
            fetchChats()
        } catch {
            addAlert({ title: 'Не удалось удалить из чата', type: 'error' })
        }
        setRemoveModal(null)
    }

    if (isLoading) return (
        <div className={classes.preloaderContainer}>
            <Preloader fullScreen="center" />
        </div>
    )

    return (
        <>
            <div className={classes.learningDynamicsContainer}>
                <div className={styles['chats-header']}>
                    <div>Здесь представлены все групповые чаты, в которые добавлен пользователь.</div>
                    <Button
                        newDesign
                        className={classNames(classes.secondaryButton, classes.truncateText, classes.textSemiBold)}
                        onClick={() => setAddModal(true)}
                    >
                        + Добавить в чат
                    </Button>
                </div>

                <ChatsHeaderRow />

                {chats.length === 0 && (
                    <div className={classes.learningDynamicsExpandedRowContainer}>
                        <div className={classes.learningDynamicsCell} style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
                            Пользователь не добавлен ни в один чат
                        </div>
                    </div>
                )}

                {chats.map((chat) => (
                    <ChatRow
                        key={chat.id}
                        chat={chat}
                        studentId={studentId}
                        onEdit={() => handleEditOpen(chat)}
                        onRemove={() => handleRemoveOpen(chat)}
                    />
                ))}
            </div>

            <Modal active={addModal} setActive={() => setAddModal(false)} className={classes.modalWrapper}>
                <ModalContainer closeModal={() => setAddModal(false)}>
                    <AddChatModal
                        studentId={studentId}
                        onClose={() => setAddModal(false)}
                        onSave={handleAddSave}
                    />
                </ModalContainer>
            </Modal>

            <Modal active={editModal !== null} setActive={() => setEditModal(null)} className={classes.modalWrapper}>
                {editModal && (
                    <ModalContainer closeModal={() => setEditModal(null)}>
                        <EditRoleModal
                            chat={editModal}
                            onClose={() => setEditModal(null)}
                            onSave={handleEditSave}
                        />
                    </ModalContainer>
                )}
            </Modal>

            <Modal active={removeModal !== null} setActive={() => setRemoveModal(null)} className={classes.modalWrapper}>
                <ActionModal
                    title="Удалить из чата"
                    content={`Вы уверены, что хотите удалить пользователя из чата «${removeModal?.name}»?`}
                    onDismiss={() => setRemoveModal(null)}
                    onAction={handleRemoveConfirm}
                />
            </Modal>
        </>
    )
}

export default StudentChatsTab
